import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import { TableOpt } from '@/components/cmTable/types'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { formatTimeStamp } from '@/utils/time'
import { getVirusMd5 } from '@/utils/format'
import { getStatusColor, getStatusTitle as getStatusTitleI18n } from '@/services/parse-status.service'
import { ProcessResult } from '@/views/dashboard/safe-status/loaris-trojan-remover/_enum/process-result'
import { formatMapToOptions } from '@/utils/tool'
import { riskTypeLabels, riskTypeSearchLabels } from '../../_enum/risk-type'
import SafetyTags from '@/components/safety-tags/safety-tags.vue'
import {
  RemoverLogList,
  TrojanItem,
  TrojanService,
} from '@/views/dashboard/safe-status/loaris-trojan-remover/loaris-trojan-remover.service'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import HandleRiskItem from '../HandleRiskItem.vue'

type RowExtra = RemoverLogList & {
  _rowId: string
  file_md5: string
  path: string
  file_size_display: string
  capture_engine: string
  risk_desc_text: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'last-remover-tab',
  props: {
    trojanInfo: { default: () => ({}) as TrojanItem }
  },
  emits: ["changeCount", "refresh"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const { t, locale } = useI18n()
const injector = useReflectiveInjector([TrojanService])
const trojanService = injector.get(TrojanService)

const tableRef = ref(null)
const { refreshList } = useCmTableListRefresh(tableRef)

const pageOption = { pageNumName: 'page', pageSizeName: 'limit' }

const form = reactive({
  key_word: '',
  scan_type: undefined as number | undefined,
  /** 筛选项值：与历史一致，为 mergeMap 后的 JSON 字符串 */
  risk_type: undefined as string | undefined,
  risk_item: undefined as number | undefined,
  handle_status: 1,
  sort: 'scan_time',
  asc: false,
})

const visible = ref(false)
const actionType = ref(1)
const single = ref(false)
const selectedList = ref<RemoverLogList[]>([])

function getStatusTitle (score: number | string) {
  void locale.value
  return getStatusTitleI18n(score, t)
}

const riskTypeLabelI18n = computed(() => {
  void locale.value
  return riskTypeLabels(t)
})

const riskTypeSearchLabelI18n = computed(() => {
  void locale.value
  return riskTypeSearchLabels(t)
})

const scanTypeLabelI18n = computed(() => {
  void locale.value
  return {
    1: t('safeProtectionModule.lightningScan'),
    2: t('safeProtectionModule.fullDiskScan'),
    3: t('safeProtectionModule.scanTypeDefense'),
    4: t('safeProtectionModule.scanTypeCustom'),
  }
})

const processResultLabelI18n = computed(() => {
  void locale.value
  return {
    1: t('safeProtectionModule.processUntreated'),
    2: t('safeProtectionModule.processProcessed'),
    3: t('safeProtectionModule.processUntreated'),
    4: t('safeProtectionModule.processTrusted'),
  }
})

const riskItemLabelMap = computed(() => {
  void locale.value
  const m: Record<number, string> = {}
  for (let i = 0; i <= 22; i++) {
    m[i] = t(`responseHandle.riskItem${i}`)
  }
  return m
})

const riskTypeOptions = computed(() => formatMapToOptions(riskTypeSearchLabelI18n.value))
const riskItemOptions = computed(() => formatMapToOptions(riskItemLabelMap.value, true))

/** 栏外筛选项：变更后立即请求（与 SearchFilterBar skill 中栏外 + events.change 一致） */
const filterFormColumns = computed<ObjectFormItem<any, any>>(() => {
  void locale.value
  return {
    risk_type: {
      prop: 'risk_type',
      label: t('safeProtectionModule.riskType'),
      type: FormType.Select,
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      colOpt: { style: 'width: 168px; flex: 0 0 auto' },
      dictData: riskTypeOptions.value,
      selectOpt: { clearable: true, filterable: true },
      events: {
        change: () => void refreshAfterFilter(),
      },
    },
    risk_item: {
      prop: 'risk_item',
      label: t('safeProtectionModule.riskItem'),
      type: FormType.Select,
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      colOpt: { style: 'width: 168px; flex: 0 0 auto' },
      dictData: riskItemOptions.value,
      selectOpt: { clearable: true, filterable: true },
      events: {
        change: () => void refreshAfterFilter(),
      },
    },
  }
})

function dataFormateRows (rows: RemoverLogList[]): RowExtra[] {
  return (rows || []).map((row, i) => {
    const md5 = getVirusMd5(row as RemoverLogList & { md5?: string })
    const path = (row.virus_info?.payload?.['文件路径'] as string) || ''
    const size = row.virus_info?.payload?.['文件大小']
    return {
      ...row,
      _rowId: `${row.scan_time}-${row.risk_item}-${i}-${md5}`.slice(0, 120),
      file_md5: md5,
      path,
      file_size_display: size || '--',
      capture_engine: row.virus_info?.engine ?? '--',
      risk_desc_text: `【${row.risk_name}】${row.virus_info?.description ?? ''}`,
    }
  })
}

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  const cols: TableOpt['columns'] = [
    {
      prop: 'risk_type',
      label: t('safeProtectionModule.riskType'),
      minWidth: 200
    },
    { label: t('ransomwareIncident.threatType'), prop: 'tags', minWidth: 230 },
    {
      prop: 'risk_item',
      label: t('safeProtectionModule.riskItem'),
      minWidth: 200
    },
  ]
  if (!HIDE_FUNC.threatIntel) {
    cols.push({
      prop: 'kscore',
      label: t('safeProtectionModule.threatStatus'),
      minWidth: 160
    })
  }
  cols.push(
    {
      prop: 'risk_desc_text',
      label: t('safeProtectionModule.riskDesc'),
      minWidth: 200,
      showOverflow: true,
    },
    {
      prop: 'handle_status',
      label: t('safeProtectionModule.processResult'),
      minWidth: 140
    },
    {
      prop: 'scan_time',
      label: t('safeProtectionModule.discoverTime'),
      minWidth: 140,
      columnOpt: { sortable: 'custom' },
      formatter: (row) =>
        row.scan_time ? formatTimeStamp(row.scan_time, 'YYYY-MM-DD HH:mm') : '--',
    },
    {
      prop: 'scan_type',
      label: t('safeProtectionModule.scanType'),
      minWidth: 200
    },
    {
      prop: 'file_md5',
      label: t('listColumns.fileMd5'),
      type: 'MD5',
      fieldKey: 'file_md5',
      minWidth: 200,
      showOverflow: true,
    },
    {
      prop: 'capture_engine',
      label: t('safeProtectionModule.captureEngine'),
      minWidth: 200,
      showOverflow: true,
    },
    {
      prop: 'path',
      label: t('listColumns.filePath'),
      type: 'PATH',
      fieldKey: 'path',
      minWidth: 200
    },
    {
      prop: 'file_size_display',
      label: t('listColumns.fileSize'),
      minWidth: 200,
      showOverflow: true,
    },
  )

  return {
    selection: true,
    rowKey: '_rowId',
    path: 'list',
    totalPath: 'total',
    height: '100%',
    showSearch: false,
    showForm: false,
    showActionBar: false,
    showRightToolbar: false,
    bodyStyle: 'padding: 0; display: flex; flex-direction: column; flex: 1; min-height: 0',
    containerOpt: { style: 'flex: 1; min-height: 0; overflow: hidden' },
    columns: cols,
  }
})

async function getListApi (params: Record<string, unknown>) {
  const deviceId = props.trojanInfo?.device_id
  const taskId = props.trojanInfo?.task_id
  if (!deviceId) {
    emit('changeCount', 0)
    return { list: [] as RemoverLogList[], total: 0 }
  }

  const riskTypeStr = params.risk_type as string | undefined
  const riskItem = params.risk_item
  const res = await trojanService.getVirusScanRiskView({
    key_word: (params.key_word as string) || '',
    scan_type: (params.scan_type as number) || undefined,
    risk_types: riskTypeStr ? JSON.parse(riskTypeStr) : undefined,
    risk_item: riskItem === undefined || riskItem === '' ? -1 : Number(riskItem),
    handle_status: (params.handle_status as number) || undefined,
    page: Number(params.page) || 1,
    limit: Number(params.limit) || 20,
    sort: (params.sort as string) || 'scan_time',
    asc: Boolean(params.asc),
    task_id: taskId,
    device_id: deviceId,
  })
  const total = +(res.total || 0)
  emit('changeCount', total)
  return res
}

function onSelectionChange (selection: RemoverLogList[]) {
  selectedList.value = selection
}

function openBatchHandle () {
  visible.value = true
  single.value = false
  actionType.value = 1
}

async function refreshAfterFilter () {
  await refreshList()
  emit('refresh')
}

async function handleSearch (searchVal: string) {
  form.key_word = (searchVal ?? '').trim()
  await nextTick()
  await refreshList()
  emit('refresh')
}

async function handleQuery () {
  await refreshList()
  emit('refresh')
}

async function handleBarReset () {
  form.key_word = ''
  form.risk_type = undefined
  form.risk_item = undefined
  form.handle_status = 1
  form.sort = 'scan_time'
  form.asc = false
  await nextTick()
  await refreshList()
  emit('refresh')
}

async function onHandleRiskRefresh () {
  await refreshList()
  await nextTick()
  try {
    tableRef.value?.tableRef?.clearSelection?.()
  } catch {
    // 表格未挂载或已销毁时忽略
  }
  selectedList.value = []
  emit('refresh')
}

watch(
  () => props.trojanInfo?.device_id,
  (nv, lv) => {
    if (nv && nv !== lv) {
      void refreshList()
    }
  },
  { immediate: true },
)

__expose({
  refresh: async () => {
    await refreshList()
    emit('refresh')
  },
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tag = _resolveComponent("el-tag")!

  return (_openBlock(), _createBlock(CmTable, {
    ref_key: "tableRef",
    ref: tableRef,
    modelValue: form,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form) = $event)),
    "table-opt": tableOpt.value,
    api: getListApi,
    "page-option": pageOption,
    "data-formate": dataFormateRows,
    "first-loading": false,
    onChangeSelected: onSelectionChange
  }, {
    header: _withCtx(() => [
      _createVNode(SearchFilterBar, {
        modelValue: form,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form) = $event)),
        "form-columns": filterFormColumns.value,
        "show-search": true,
        "sync-search-from-field": "key_word",
        "search-placeholder": _ctx.$t('common.phMd5Risk'),
        "popover-threshold": 2,
        onSearch: handleSearch,
        onQuery: handleQuery,
        onReset: handleBarReset
      }, {
        actions: _withCtx(() => [
          _createVNode(_component_el_button, {
            size: "small",
            type: "primary",
            disabled: !selectedList.value.length,
            onClick: openBatchHandle
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('safeProtectionModule.processNow')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _: 1
      }, 8, ["modelValue", "form-columns", "search-placeholder"])
    ]),
    risk_type: _withCtx(({ row }) => [
      _createTextVNode(_toDisplayString(riskTypeLabelI18n.value[row.risk_type] ?? '--'), 1)
    ]),
    risk_item: _withCtx(({ row }) => [
      _createTextVNode(_toDisplayString(riskItemLabelMap.value[row.risk_item] ?? '--'), 1)
    ]),
    kscore: _withCtx(({ row }) => [
      _createVNode(_component_el_tag, {
        size: "small",
        style: _normalizeStyle([{"border":"none","color":"#fff"}, { background: _unref(getStatusColor)(row.kscore) }]),
        class: "cm-score-tag"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(getStatusTitle(row.kscore)), 1)
        ]),
        _: 2
      }, 1032, ["style"])
    ]),
    risk_desc_text: _withCtx(({ row }) => [
      _createTextVNode(_toDisplayString(row.risk_desc_text), 1)
    ]),
    handle_status: _withCtx(({ row }) => [
      _createTextVNode(_toDisplayString(processResultLabelI18n.value[row.handle_status] ?? '--'), 1)
    ]),
    scan_type: _withCtx(({ row }) => [
      _createTextVNode(_toDisplayString(scanTypeLabelI18n.value[row.scan_type] ?? '--'), 1)
    ]),
    tags: _withCtx(({ value }) => [
      (value && value.length)
        ? (_openBlock(), _createBlock(SafetyTags, {
            key: 0,
            data: value,
            max: 2
          }, null, 8, ["data"]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createTextVNode(" -- ")
          ], 64))
    ]),
    action: _withCtx(() => [
      _createVNode(HandleRiskItem, {
        "process-type": _unref(ProcessResult).Untreated,
        "action-type": actionType.value,
        "onUpdate:actionType": _cache[1] || (_cache[1] = ($event: any) => ((actionType).value = $event)),
        modelValue: visible.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((visible).value = $event)),
        single: single.value,
        "onUpdate:single": _cache[3] || (_cache[3] = ($event: any) => ((single).value = $event)),
        device_id: _ctx.trojanInfo.device_id,
        system_type: _ctx.trojanInfo.system_type,
        "selected-list": selectedList.value,
        onRefresh: onHandleRiskRefresh
      }, null, 8, ["process-type", "action-type", "modelValue", "single", "device_id", "system_type", "selected-list"])
    ]),
    _: 1
  }, 8, ["modelValue", "table-opt"]))
}
}

})